import React, { ChangeEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EnrichedPaymentTerminal } from 'subdomains/cpo/types/payment-terminals';
import { request } from 'utils/api';
import { PAYMENT_TERMINALS_V1_SEARCH_API_URL } from 'subdomains/cpo/router/api';

import {
  Input,
  Table,
  Segment,
  PaginationProps,
  Message,
} from 'semantic-ui-react';
import Stack from 'common/layout/helpers/Stack';
import Flex from 'common/layout/helpers/Flex';
import Group from 'common/layout/helpers/Group';
import { Link } from 'react-router-dom';
import { useDebounce } from 'subdomains/cpo/hooks/useDebounce';
import { QueryResultsTotal } from 'subdomains/cpo/components/QueryResultsCount';
import { QueryNoResultsFallback } from 'subdomains/cpo/components/QueryNoResultsFallback';
import { QueryErrorFallback } from 'subdomains/cpo/components/QueryErrorFallback';
import { PAGE_LIMIT } from 'subdomains/cpo/config/pagination';
import { TablePagination } from 'subdomains/cpo/components/TablePagination';
import { StatusLabel } from 'subdomains/cpo/screens/payment-terminals/components/StatusLabel';
import { ExportFormat } from 'subdomains/cpo/types/exports';
import { PaymentTerminalActions } from 'subdomains/cpo/screens/payment-terminals/components/PaymentTerminalActions';
import { useUser } from 'contexts/user';
import {
  hasPaymentTerminalPermissions,
  hasPaymentTerminalPlatformFeature,
} from 'screens/Location/PaymentTerminals/utils';
import { PaymentTerminalsHeader } from '../../components/PaymentTerminalsHeader';
import { SegmentLoader } from 'subdomains/cpo/components/SegmentLoader';

export function PaymentTerminals() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const { user, provider } = useUser();
  const { hasFeature, loading: hasFeatureLoading } =
    hasPaymentTerminalPlatformFeature();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  const {
    data: res,
    isPending,
    error,
    refetch,
  } = useQuery<{
    data: EnrichedPaymentTerminal[];
    meta: { total: number; limit: number };
  }>({
    queryKey: [
      PAYMENT_TERMINALS_V1_SEARCH_API_URL,
      currentPage,
      debouncedSearchTerm,
    ],
    queryFn: async () => {
      const { data, meta } = await request<{
        data: EnrichedPaymentTerminal[];
        meta: { total: number; limit: number };
      }>({
        path: PAYMENT_TERMINALS_V1_SEARCH_API_URL,
        method: 'POST',
        body: {
          ...(debouncedSearchTerm && {
            keywords: debouncedSearchTerm,
          }),
          sort: { field: 'createdAt', order: 'desc' },
          limit: PAGE_LIMIT,
          skip: (currentPage - 1) * PAGE_LIMIT,
        },
      });

      return { data, meta };
    },
  });

  if (!user || !provider || hasFeatureLoading) {
    return <PaymentTerminalsHeader loading={hasFeatureLoading} />;
  }

  const canRead =
    hasFeature && hasPaymentTerminalPermissions(user, 'read', provider);
  if (!canRead)
    return (
      <Message
        error
        content={t(
          'paymentTerminals.noFeature',
          'You do not have access to this feature.'
        )}
      />
    );

  const paymentTerminalsData = res?.data ?? [];
  const total = res?.meta?.total ?? 0;

  const shouldRenderNoResults =
    !isPending && !error && paymentTerminalsData.length === 0;

  const shouldRenderTable =
    paymentTerminalsData.length > 0 && !isPending && !error;

  const shouldRenderError = error && !isPending;

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (
    e: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) => {
    setCurrentPage(data.activePage as number);
  };

  const handleExport = async (format: ExportFormat) => {
    return request({
      path: PAYMENT_TERMINALS_V1_SEARCH_API_URL,
      method: 'POST',
      body: {
        format,
        ...(debouncedSearchTerm && { keywords: debouncedSearchTerm }),
        sort: { field: 'createdAt', order: 'desc' },
        limit: PAGE_LIMIT,
        skip: (currentPage - 1) * PAGE_LIMIT,
      },
    });
  };

  return (
    <>
      <PaymentTerminalsHeader
        loading={hasFeatureLoading}
        handleExport={handleExport}
      />
      <Segment>
        <Flex justify="flex-end">
          <Group align="center">
            <QueryResultsTotal total={total} />
            <Input
              type="text"
              placeholder={t('paymentTerminals.SearchPlaceholder', 'Search...')}
              onChange={handleSearchInputChange}
            />
          </Group>
        </Flex>
      </Segment>

      {shouldRenderError && <QueryErrorFallback message={error.message} />}
      {shouldRenderNoResults && <QueryNoResultsFallback />}
      {isPending && <SegmentLoader />}

      {shouldRenderTable && (
        <>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={4}>
                  {t('paymentTerminals.PaymentTerminalCol', 'Payment Terminal')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {t('paymentTerminals.TypelCol', 'Type')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {t('paymentTerminals.OwnerCol', 'Owner')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {t('paymentTerminals.AcquiringCol', 'Acquiring')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {t('paymentTerminals.StatusCol', 'Status')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {t('paymentTerminals.Actions', 'Actions')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paymentTerminalsData.map((terminal) => (
                <Table.Row key={terminal.id}>
                  <Table.Cell>
                    <Stack>
                      {terminal.name}
                      {terminal.payterDetails?.serialNumber && (
                        <small style={{ fontWeight: 'bold' }}>
                          <Group>
                            {t(
                              'paymentTerminals.SerialNumber',
                              'Serial Number:'
                            )}{' '}
                            {terminal.payterDetails?.serialNumber}
                          </Group>
                        </small>
                      )}
                    </Stack>
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center' }}>
                    {terminal.type}
                  </Table.Cell>
                  <Table.Cell>
                    <Stack>
                      <div>
                        {t('paymentTerminal.account', 'Account:')}
                        <Link to={`/accounts/${terminal.accountId}`}>
                          {' '}
                          {terminal?.account?.name}
                        </Link>
                      </div>
                      <div></div>
                      <div>
                        {t('paymentTerminal.location', 'Location:')}
                        <Link
                          to={`/charging-stations/locations/${terminal.locationId}`}>
                          {' '}
                          {terminal?.location?.name}
                        </Link>
                      </div>
                    </Stack>
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center' }}>
                    <Stack>
                      {terminal.contract === 'road'
                        ? t('paymentTerminals.RoadContract', 'Road')
                        : t('paymentTerminals.CustomerContract', 'Customer')}
                      <small style={{ fontWeight: 'bold' }}>
                        {terminal.acquirer ? terminal.acquirer : ''}
                      </small>
                    </Stack>
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center' }}>
                    <StatusLabel
                      status={terminal.enabled ? 'enabled' : 'disabled'}
                    />
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center' }}>
                    <PaymentTerminalActions
                      paymentTerminal={terminal}
                      onDeleted={() => refetch()}
                      onUpdated={() => refetch()}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <TablePagination
            page={currentPage}
            limit={PAGE_LIMIT}
            total={total}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </>
  );
}
